import { useCallback, useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Star } from "lucide-react";

const STATUSES = ["Novo", "Contactado", "Visita agendada", "Orçamento", "Adjudicado", "Não adjudicado", "Concluído"];
const CATEGORIES = ["Remodelações", "Gestão de Obra", "Valorização de Imóveis"];
const PROPERTY_TYPES = ["", "Apartamento", "Moradia", "Loja / Escritório", "Outro"];
const EMPTY_PROJECT = { title: "", slug: "", category: "Remodelações", location: "", date: "", property_type: "", intervention_type: "", specialties: [], problem: "", works_done: "", materials: "", cover_file_id: "", gallery_ids: [], before_ids: [], during_ids: [], after_ids: [], before_after: [], published: false, featured: false };

export default function AdminProjects() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);

    const [specs, setSpecs] = useState([]);
    const load = useCallback(() => Promise.all([
        api.get("/admin/projects").then(r => setItems(r.data)),
        api.get("/admin/specialties").then(r => setSpecs(r.data)),
    ]), []);
    useEffect(() => { load(); }, [load]);

    const uploadOne = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        return data.id;
    };

    const save = async () => {
        if (!editing.title || !editing.slug) return toast.error("Título e slug obrigatórios");
        try {
            if (editing.id) await api.put(`/admin/projects/${editing.id}`, editing);
            else await api.post("/admin/projects", editing);
            toast.success("Obra guardada");
            setEditing(null); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    };

    const del = async (id) => { if (!window.confirm("Eliminar obra?")) return; await api.delete(`/admin/projects/${id}`); load(); };

    const togglePublished = async (p) => {
        try {
            await api.put(`/admin/projects/${p.id}`, { ...p, published: !p.published });
            toast.success(!p.published ? "Obra publicada — visível no site" : "Obra passou a rascunho");
            load();
        } catch { toast.error("Erro ao atualizar"); }
    };

    const toggleSpec = (name) => setEditing(e => ({...e, specialties: e.specialties.includes(name) ? e.specialties.filter(s => s !== name) : [...e.specialties, name]}));

    const uploadMany = async (files, key) => {
        const ids = [];
        for (const f of files) ids.push(await uploadOne(f));
        setEditing(e => ({...e, [key]: [...(e[key] || []), ...ids]}));
    };

    const removeFromList = (key, id) => setEditing(e => ({...e, [key]: e[key].filter(x => x !== id)}));

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="font-display text-3xl font-bold text-stone-900">Portfólio</h1>
                <button onClick={() => setEditing(EMPTY_PROJECT)} className="btn-copper !py-2 !px-4 text-sm" data-testid="admin-project-add">
                    <Plus className="w-4 h-4" /> Nova obra
                </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map(p => (
                    <div key={p.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                        {p.cover_file_id && <img src={fileUrl(p.cover_file_id)} alt="" className="w-full h-40 object-cover" />}
                        <div className="p-4">
                            <div className="text-xs text-[#B56D3B] uppercase font-medium">{p.category}</div>
                            <h3 className="font-semibold mb-1 flex items-center gap-1">{p.title} {p.featured && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}</h3>
                            <p className="text-xs text-stone-500 mb-3">
                                {p.location} · <span className={p.published ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}>{p.published ? "Publicado" : "Rascunho"}</span>
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button onClick={() => togglePublished(p)}
                                    data-testid={`toggle-publish-${p.slug}`}
                                    className={`text-xs px-3 py-1 rounded font-medium ${p.published
                                        ? "bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100"
                                        : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                                    {p.published ? "Despublicar" : "Publicar"}
                                </button>
                                <button onClick={() => setEditing({...EMPTY_PROJECT, ...p})} className="text-xs px-3 py-1 border border-stone-300 rounded">Editar</button>
                                <button onClick={() => del(p.id)} className="text-xs px-3 py-1 border border-red-200 text-red-600 rounded"><Trash2 className="w-3 h-3" /></button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && <p className="text-stone-500 col-span-full text-center py-10">Sem obras.</p>}
            </div>

            {editing && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 space-y-4">
                            <h2 className="font-display font-bold text-xl">{editing.id ? "Editar" : "Nova"} obra</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <Inp label="Título" v={editing.title} on={(v) => setEditing({...editing, title: v, slug: editing.slug || v.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g,'-')})} />
                                <Inp label="Slug (URL)" v={editing.slug} on={(v) => setEditing({...editing, slug: v})} />
                                <Sel label="Área" v={editing.category} on={(v) => setEditing({...editing, category: v})} opts={CATEGORIES} />
                                <Inp label="Localização" v={editing.location} on={(v) => setEditing({...editing, location: v})} />
                                <Sel label="Tipo de imóvel" v={editing.property_type} on={(v) => setEditing({...editing, property_type: v})} opts={PROPERTY_TYPES} />
                                <Inp label="Tipo de intervenção" v={editing.intervention_type} on={(v) => setEditing({...editing, intervention_type: v})} />
                                <Inp label="Data / Período" v={editing.date} on={(v) => setEditing({...editing, date: v})} />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Especialidades envolvidas</label>
                                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 border border-stone-200 rounded">
                                    {specs.map(s => (
                                        <button key={s.id} type="button" onClick={() => toggleSpec(s.name)}
                                            className={`px-2.5 py-1 rounded-full text-xs border ${editing.specialties.includes(s.name) ? "bg-[#18352F] text-white border-[#18352F]" : "bg-white border-stone-300"}`}>{s.name}</button>
                                    ))}
                                </div>
                            </div>
                            <Area label="Descrição / Objetivo do cliente" v={editing.problem} on={(v) => setEditing({...editing, problem: v})} />
                            <Area label="Trabalhos realizados" v={editing.works_done} on={(v) => setEditing({...editing, works_done: v})} />
                            <Area label="Materiais / soluções utilizadas" v={editing.materials} on={(v) => setEditing({...editing, materials: v})} />

                            <FileGroup label="Fotografia principal (capa)" ids={editing.cover_file_id ? [editing.cover_file_id] : []}
                                onAdd={async (files) => { const id = await uploadOne(files[0]); setEditing({...editing, cover_file_id: id}); }}
                                onRemove={() => setEditing({...editing, cover_file_id: ""})} single />

                            <FileGroup label="Fotografias — ANTES" ids={editing.before_ids} onAdd={(files) => uploadMany(Array.from(files), "before_ids")} onRemove={(id) => removeFromList("before_ids", id)} />
                            <FileGroup label="Fotografias — DURANTE" ids={editing.during_ids} onAdd={(files) => uploadMany(Array.from(files), "during_ids")} onRemove={(id) => removeFromList("during_ids", id)} />
                            <FileGroup label="Fotografias — DEPOIS" ids={editing.after_ids} onAdd={(files) => uploadMany(Array.from(files), "after_ids")} onRemove={(id) => removeFromList("after_ids", id)} />
                            <FileGroup label="Galeria adicional" ids={editing.gallery_ids} onAdd={(files) => uploadMany(Array.from(files), "gallery_ids")} onRemove={(id) => removeFromList("gallery_ids", id)} />

                            <div className="flex gap-6">
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({...editing, published: e.target.checked})} className="accent-emerald-700" />
                                    Publicado
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({...editing, featured: e.target.checked})} className="accent-amber-600" />
                                    Destacar na homepage
                                </label>
                            </div>

                            <div className="flex gap-2 pt-4 sticky bottom-0 bg-white pb-2">
                                <button onClick={save} className="btn-copper flex-1 justify-center">Guardar</button>
                                <button onClick={() => setEditing(null)} className="px-6 py-2 border border-stone-300 rounded-full">Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const Inp = ({label, v, on}) => (
    <label className="block text-sm">
        <span className="text-xs uppercase tracking-wider text-stone-500">{label}</span>
        <input value={v || ""} onChange={(e) => on(e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded" />
    </label>
);
const Sel = ({label, v, on, opts}) => (
    <label className="block text-sm">
        <span className="text-xs uppercase tracking-wider text-stone-500">{label}</span>
        <select value={v || ""} onChange={(e) => on(e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded">
            {opts.map(o => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
    </label>
);
const Area = ({label, v, on}) => (
    <label className="block text-sm">
        <span className="text-xs uppercase tracking-wider text-stone-500">{label}</span>
        <textarea rows={2} value={v || ""} onChange={(e) => on(e.target.value)} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded" />
    </label>
);
function FileGroup({ label, ids, onAdd, onRemove, single }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider text-stone-500 block mb-1">{label}</label>
            <div className="flex gap-2 flex-wrap mb-2">
                {ids.map(id => (
                    <div key={id} className="relative">
                        <img src={fileUrl(id)} alt="" className="w-20 h-20 object-cover rounded" />
                        <button type="button" onClick={() => onRemove(id)} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                    </div>
                ))}
            </div>
            <input type="file" accept="image/*" multiple={!single} onChange={(e) => e.target.files.length && onAdd(e.target.files)} />
        </div>
    );
}
